import { PaymentElement } from "@stripe/react-stripe-js";
import { useState } from "react";
import { useStripe, useElements } from "@stripe/react-stripe-js";
import Cookies from "js-cookie";
import config from '../Config';
import { HandleResponse } from "../functions/HandleResponse";
import { authFetch } from "../functions/authFetch";
import { useNavigate } from "react-router-dom";
import './pages.css';

export default function CheckoutForm() {
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();

  const [message, setMessage] = useState(null);
  const [saveMethod, setSaveMethod] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showCompletePopup, setShowCompletePopup] = useState(false);
  const [showErrorPopup, setShowErrorPopup] = useState(false);
  const [creditResult, setCreditResult] = useState(null);

  // Billing detail fields (controlled)
  const [billingName, setBillingName] = useState('');
  const [billingEmail, setBillingEmail] = useState('');
  const [billingAddress, setBillingAddress] = useState('');
  const [billingCity, setBillingCity] = useState('');
  const [billingZip, setBillingZip] = useState('');
  const [billingCountry, setBillingCountry] = useState('');

  const notifyComplete = async (amount) => {
    try {
      const token = Cookies.get("runner_app");
      const response = await authFetch(`${config.AUTH_API}/v1/add-credit?amount=${amount}`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error("Failed to update balance");
      // Carries the credited amount and the resulting balance.
      setCreditResult(await response.json().catch(() => null));
    } catch (err) {
      console.error("Error updating balance:", err);
    }
  };

  const savePaymentMethod = async (paymentMethodId) => {
    try {
      const token = Cookies.get("runner_app");
      const response = await authFetch(
        `${config.AUTH_API}/v1/save-payment-method?paymentMethodId=${paymentMethodId}`,
        { method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` } }
      );
      await HandleResponse(response, navigate);
    } catch (err) {
      console.error("Error saving payment method:", err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setIsProcessing(true);

    try {
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          payment_method_data: {
            billing_details: {
              name: billingName,
              email: billingEmail,
              address: {
                line1: billingAddress,
                city: billingCity,
                postal_code: billingZip,
                country: billingCountry,
              },
            },
          },
        },
        redirect: "if_required",
      });

      if (error) {
        setMessage(error.type === "card_error" || error.type === "validation_error"
          ? error.message
          : "An unexpected error occurred.");
        setShowErrorPopup(true);
      } else if (paymentIntent?.status === "succeeded") {
        if (saveMethod) await savePaymentMethod(paymentIntent.payment_method);
        await notifyComplete(paymentIntent.amount);
        setShowCompletePopup(true);
      }
    } catch (err) {
      console.error("Unexpected error:", err);
      setMessage("An unexpected error occurred.");
      setShowErrorPopup(true);
    }

    setIsProcessing(false);
  };

  return (
    <form className="bill-checkout-form" onSubmit={handleSubmit}>
      <div className="bill-checkout-section-label">Card Details</div>
      <PaymentElement id="payment-element" />

      <div className="bill-checkout-section-label" style={{ marginTop: '1.5rem' }}>
        Billing Information
      </div>

      <div className="bill-checkout-fields">
        <div className="bill-checkout-field bill-checkout-field--full">
          <label>Full Name</label>
          <input type="text" value={billingName} onChange={e => setBillingName(e.target.value)}
            placeholder="Jane Smith" required className="bill-checkout-input" />
        </div>
        <div className="bill-checkout-field bill-checkout-field--full">
          <label>Email</label>
          <input type="email" value={billingEmail} onChange={e => setBillingEmail(e.target.value)}
            placeholder="jane@example.com" required className="bill-checkout-input" />
        </div>
        <div className="bill-checkout-field bill-checkout-field--full">
          <label>Address</label>
          <input type="text" value={billingAddress} onChange={e => setBillingAddress(e.target.value)}
            placeholder="123 Main St" required className="bill-checkout-input" />
        </div>
        <div className="bill-checkout-field">
          <label>City</label>
          <input type="text" value={billingCity} onChange={e => setBillingCity(e.target.value)}
            placeholder="New York" required className="bill-checkout-input" />
        </div>
        <div className="bill-checkout-field">
          <label>ZIP Code</label>
          <input type="text" value={billingZip} onChange={e => setBillingZip(e.target.value)}
            placeholder="10001" required className="bill-checkout-input" />
        </div>
        <div className="bill-checkout-field bill-checkout-field--full">
          <label>Country</label>
          <input type="text" value={billingCountry} onChange={e => setBillingCountry(e.target.value)}
            placeholder="US" required className="bill-checkout-input" />
        </div>
      </div>

      <label className="bill-save-method">
        <input type="checkbox" checked={saveMethod} onChange={e => setSaveMethod(e.target.checked)} />
        <span>Save this card for future payments</span>
      </label>

      <button
        type="submit"
        disabled={isProcessing || !stripe || !elements}
        className="bill-submit-btn"
        style={{ marginTop: '1.25rem' }}
      >
        {isProcessing ? 'Processing…' : 'Pay Now'}
      </button>

      {/* Success modal */}
      {showCompletePopup && (
        <div className="bill-modal-overlay">
          <div className="bill-modal">
            <div className="bill-modal-icon bill-modal-icon--success">✓</div>
            <h3 className="bill-modal-title">Payment Successful</h3>
            <p className="bill-modal-body">
              {creditResult?.balance != null ? (
                <>Your credits have been added. Balance: ${(creditResult.balance / 100).toFixed(2)}.</>
              ) : (
                <>Your credits have been added to your account.</>
              )}
            </p>
            <button className="bill-modal-btn" onClick={() => { setShowCompletePopup(false); navigate('/jobrunner'); }}>
              Continue
            </button>
          </div>
        </div>
      )}

      {/* Error modal */}
      {message && showErrorPopup && (
        <div className="bill-modal-overlay">
          <div className="bill-modal">
            <div className="bill-modal-icon">⚠</div>
            <h3 className="bill-modal-title">Payment Failed</h3>
            <p className="bill-modal-body">{message}</p>
            <button className="bill-modal-btn" onClick={() => setShowErrorPopup(false)}>
              Try Again
            </button>
          </div>
        </div>
      )}
    </form>
  );
}
